import { CreateFunnel } from 'components';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Intro, Deceased, Customer, OfferNumber, Signature, Summary } from 'containers/Steps';
import { Loader } from 'mymoria-ui/components';
import * as Yup from 'yup';
import { global } from 'data';
import { toInteger } from 'lodash';
import moment from 'moment';
import { Poa } from 'types/index';
import config from 'config';
import { api } from 'utils';
import { useToast } from 'mymoria-ui/hooks';
import { getValidPhoneFormat } from 'mymoria-ui';

/* move the dateFormat to config file */
const OptDateSchema = Yup.string().test(
  'valid',
  global.validators.date,
  value =>
    value === '' ||
    value === undefined ||
    value === 'TT.MM.JJJJ' ||
    moment(value, config.dateFormat.view, true).isValid(),
);
const DateSchema = OptDateSchema.required(global.validators.required);
const NumberSchema = Yup.string()
  .typeError(global.validators.number)
  .test('number', global.validators.number, val => (val ? /^\d+$/.test(val) : false));

const RequiredNumber = NumberSchema.required(global.validators.number);

const makeDateInt = (dateString?: string) =>
  dateString
    ? toInteger(
        moment(dateString, config.dateFormat.view)
          .set({ hour: 12 })
          .format(config.dateFormat.timeStamp),
      )
    : undefined;

const Main: FunctionComponent<RouteComponentProps> = ({ match: { path } }) => {
  const history = useHistory();
  const [isSubmitting, setIsSubmiting] = useState(false);
  const { addToast } = useToast();
  const handleSubmit = useCallback(
    (values: Poa) => {
      setIsSubmiting(true);
      api
        .post('/powerofattorney/', {
          ...values,
          customer: {
            ...values.customer,
            birthDate: makeDateInt(values.customer.birthDate),
            phone: getValidPhoneFormat({ phone: values.customer.phone, formatType: 'e164' }),
          },
          deceased: {
            ...values.deceased,
            birthDate: makeDateInt(values.deceased.birthDate),
            deathDate: makeDateInt(values.deceased.deathDate),
          },
          signature: {
            ...values.signature,
            date: makeDateInt(values.signature.date),
          },
          offerNumber: values.offerNumber ? values.offerNumber : 0,
        })
        .then(({ data: { id } }) => {
          setIsSubmiting(false);
          history.push('/overview/download', { id });
        })
        .catch((e: Error) => {
          addToast(e.message, 'error');
          history.push('/');
        });
    },
    [history, addToast],
  );

  if (isSubmitting) {
    return <Loader overlay />;
  }

  return (
    <CreateFunnel
      name="vollmacht-create-mode"
      basepath={path}
      initialValues={{
        deceased: {
          firstName: '',
          lastName: '',
          maidenName: '',
          maritalStatus: '',
          birthDate: '',
          birthPlace: '',
          deathDate: '',
          deathPlace: '',
          street: '',
          houseNr: '',
          code: '',
          place: '',
        },
        customer: {
          firstName: '',
          lastName: '',
          birthDate: '',
          relationship: '',
          street: '',
          houseNr: '',
          code: '',
          place: '',
          email: '',
          phone: '',
        },
        signature: {
          img: '',
          place: '',
          date: '',
        },
        offerNumber: '',
      }}
      steps={[
        {
          title: global.title,
          path: '/intro',
          component: Intro,
        },
        {
          title: global.title,
          path: '/deceased',
          component: Deceased,
          validationSchema: Yup.object().shape({
            deceased: Yup.object().shape({
              firstName: Yup.string().required(global.validators.required),
              lastName: Yup.string().required(global.validators.required),
              maidenName: Yup.string().optional(),
              maritalStatus: Yup.string().required(global.validators.required),
              birthDate: DateSchema.test('not in future', global.validators.date, (value: any) =>
                moment(value, config.dateFormat.view, true).isBefore(moment()),
              ),
              birthPlace: Yup.string().required(global.validators.required),
              deathDate: OptDateSchema,
              deathPlace: Yup.string(),
              street: Yup.string().required(global.validators.required),
              houseNr: Yup.string().required(global.validators.required),
              code: RequiredNumber,
              place: Yup.string().required(global.validators.required),
            }),
          }),
        },
        {
          title: global.title,
          path: '/customer',
          component: Customer,
          validationSchema: Yup.object().shape({
            customer: Yup.object().shape({
              firstName: Yup.string().required(global.validators.required),
              lastName: Yup.string().required(global.validators.required),
              birthDate: DateSchema.test('not in future', global.validators.date, (value: any) =>
                moment(value, config.dateFormat.view, true).isBefore(moment()),
              ),
              relationship: Yup.string().required(global.validators.required),
              street: Yup.string().required(global.validators.required),
              houseNr: Yup.string().required(global.validators.required),
              code: Yup.string()
                .test('number', global.validators.number, val => (val ? /^\d+$/.test(val) : false))
                .required(global.validators.code),
              place: Yup.string().required(global.validators.required),
              email: Yup.string()
                .email(global.validators.email)
                .matches(/^[A-Za-z-0-9+@._]+$/, global.validators.noLatinChar),
              phone: Yup.string().matches(/^[0][0-9_/+-]*$/, global.validators.wrongPhoneNumber),
            }),
          }),
        },
        {
          title: global.title,
          path: '/offerNumber',
          component: OfferNumber,
          validationSchema: Yup.object().shape({
            offerNumber: Yup.string()
              .test('number', global.validators.number, val => (val ? /^\d+$/.test(val) : true))
              .max(6, global.validators.sixDigits)
              .min(6, global.validators.sixDigits),
          }),
        },
        {
          title: global.title,
          path: '/signature',
          component: Signature,
          validationSchema: Yup.object().shape({
            signature: Yup.object().shape({
              img: Yup.string().required(global.validators.required),
              place: Yup.string().required(global.validators.required),
              date: DateSchema,
            }),
          }),
        },
        {
          title: global.title,
          path: '/summary',
          component: Summary,
        },
      ]}
      onSubmit={handleSubmit}
    />
  );
};

export default Main;
